import styled from 'styled-components'
import { danger, lightGrey, orange, success, textMuted, white } from '@/theme/colors'
import { fontBody } from '@/theme/fonts'
import { fieldStyles } from '@/theme/recipes'
import { motion, radii, shadows } from '@/theme/tokens'
import { fontSizes, leading } from '@/theme/typography'

export const FieldWrap = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
`

export const FieldLabel = styled.label`
	font-family: ${fontBody};
	font-weight: 500;
	font-size: ${fontSizes.caption};
	color: ${textMuted};
`

export const FieldHint = styled.span`
	font-family: ${fontBody};
	font-size: ${fontSizes.caption};
	color: ${textMuted};
`

export const FieldError = styled.span`
	font-family: ${fontBody};
	font-weight: 500;
	font-size: ${fontSizes.caption};
	color: ${danger};
`

/** The standard text input on the theme's field recipe. */
export const Input = styled.input`
	${fieldStyles}

	&[aria-invalid='true'] {
		border-color: ${danger};
	}
`

export const Textarea = styled.textarea`
	${fieldStyles}
	height: auto;
	min-height: 96px;
	padding: 10px 14px;
	line-height: ${leading.body};
	resize: vertical;

	&[aria-invalid='true'] {
		border-color: ${danger};
	}
`

/**
 * The file picker: hidden, opened from a button through its ref. The button
 * carries the accessible name; the input only holds the `accept` list.
 */
export const FileInput = styled.input.attrs({ type: 'file' })`
	display: none;
`

/** The native checkbox in the accent color. */
export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
	cursor: pointer;
	accent-color: ${orange};
`

/** The native range slider: a hairline track with the accent thumb. */
export const Range = styled.input.attrs({ type: 'range' })`
	-webkit-appearance: none;
	height: 4px;
	border-radius: ${radii.round};
	background: ${lightGrey};
	outline: none;
	cursor: pointer;

	&::-webkit-slider-thumb {
		-webkit-appearance: none;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: ${orange};
		cursor: pointer;
		transition: transform ${motion.fast};

		&:hover {
			transform: scale(1.2);
		}
	}

	&::-moz-range-thumb {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: ${orange};
		cursor: pointer;
		border: none;
	}
`

/**
 * The switch: a native checkbox kept in the tab order but out of sight, and the
 * track that draws its state. Pair them by id / htmlFor inside a relative wrapper.
 */
export const SwitchInput = styled.input.attrs({ type: 'checkbox' })`
	opacity: 0;
	width: 0;
	height: 0;
	position: absolute;
`

export const SwitchTrack = styled.label<{ $checked: boolean; $disabled: boolean }>`
	display: block;
	width: 44px;
	height: 24px;
	background: ${({ $checked }) => ($checked ? success : lightGrey)};
	border-radius: ${radii.round};
	cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
	transition: background ${motion.base};
	opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
	position: relative;

	&::after {
		content: '';
		position: absolute;
		top: 3px;
		left: ${({ $checked }) => ($checked ? '23px' : '3px')};
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background: ${white};
		transition: left ${motion.base};
		box-shadow: ${shadows.inner};
	}
`
